import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import {
  User, MapPin, Briefcase, Settings, ChevronRight,
  CheckCircle, Loader, Save, ArrowLeft, Mail, Phone, Shield, Search
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SEGMENTS = [
  'Moda e Vestuário', 'Eletrônicos', 'Alimentos e Bebidas', 'Saúde e Beleza',
  'Casa e Decoração', 'Esportes', 'Livros e Educação', 'Artesanato',
  'Pet Shop', 'Serviços Digitais', 'Jogos e Games', 'Outro'
];

const STATES = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS',
  'MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'
];

const fmt = {
  cpf: (v) => { const d = v.replace(/\D/g,'').slice(0,11); if(d.length<=3)return d; if(d.length<=6)return `${d.slice(0,3)}.${d.slice(3)}`; if(d.length<=9)return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}`; return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`; },
  cnpj: (v) => { const d = v.replace(/\D/g,'').slice(0,14); if(d.length<=2)return d; if(d.length<=5)return `${d.slice(0,2)}.${d.slice(2)}`; if(d.length<=8)return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5)}`; if(d.length<=12)return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8)}`; return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12)}`; },
  phone: (v) => { const d = v.replace(/\D/g,'').slice(0,11); if(d.length<=2)return `(${d}`; if(d.length<=7)return `(${d.slice(0,2)}) ${d.slice(2)}`; return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`; },
  cep: (v) => { const d = v.replace(/\D/g,'').slice(0,8); if(d.length<=5)return d; return `${d.slice(0,5)}-${d.slice(5)}`; },
};

const STEPS = [
  { id: 'personal', title: 'Dados Pessoais', icon: User },
  { id: 'address', title: 'Endereço', icon: MapPin },
  { id: 'business', title: 'Negócio', icon: Briefcase },
  { id: 'config', title: 'Configurações', icon: Settings },
];

// ─── Verification Code Input ──────────────────────────────────
const CodeInput = ({ onSubmit, loading, label }) => {
  const [code, setCode] = useState('');
  return (
    <div className="flex items-center gap-2 mt-2">
      <Input
        value={code}
        onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        placeholder="000000"
        className="w-32 font-mono text-center tracking-widest"
        maxLength={6}
        data-testid="verification-code-input"
      />
      <Button
        size="sm"
        onClick={() => onSubmit(code)}
        disabled={code.length < 6 || loading}
        className="bg-emerald-600 hover:bg-emerald-700 text-xs"
        data-testid="verify-code-btn"
      >
        {loading ? <Loader className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
        Verificar
      </Button>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────
export const SellerCompleteProfile = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    full_name: '', store_name: '', document_type: 'cpf', document_number: '',
    phone: '', address: { cep: '', street: '', number: '', complement: '', neighborhood: '', city: '', state: '' },
    business_segment: '', business_description: '', storefront_name: '', main_category: '',
  });

  // Verification states
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingPhone, setSendingPhone] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [verifyingPhone, setVerifyingPhone] = useState(false);
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [phoneCodeSent, setPhoneCodeSent] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [cepLoading, setCepLoading] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const [profileRes, userRes] = await Promise.all([
        axios.get(`${API_URL}/seller/profile`, { withCredentials: true }),
        axios.get(`${API_URL}/auth/me`, { withCredentials: true }).catch(() => null)
      ]);
      const d = profileRes.data;
      setProfile(prev => ({
        ...prev,
        full_name: d.full_name || '', store_name: d.store_name || '',
        document_type: d.document_type || 'cpf', document_number: d.document_number || '',
        phone: d.phone || '', address: d.address || prev.address,
        business_segment: d.business_segment || '', business_description: d.business_description || '',
        storefront_name: d.storefront_name || '', main_category: d.main_category || '',
      }));
      setPhoneVerified(d.phone_verified || false);
      if (userRes?.data) {
        setUserEmail(userRes.data.email || '');
        setEmailVerified(userRes.data.email_verified || false);
      }
    } catch (e) { /* empty */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const updateField = (field, value) => setProfile(p => ({ ...p, [field]: value }));
  const updateAddress = (field, value) => setProfile(p => ({ ...p, address: { ...p.address, [field]: value } }));

  // ── Email verification ──
  const handleSendEmailCode = async () => {
    if (!userEmail) { toast.error('Email não encontrado'); return; }
    setSendingEmail(true);
    try {
      await axios.post(`${API_URL}/verify/email/send`, { email: userEmail }, { withCredentials: true });
      setEmailCodeSent(true);
      toast.success('Código enviado para seu email!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar código');
    } finally { setSendingEmail(false); }
  };

  const handleVerifyEmail = async (code) => {
    setVerifyingEmail(true);
    try {
      await axios.post(`${API_URL}/verify/email/check`, { email: userEmail, code }, { withCredentials: true });
      setEmailVerified(true);
      setEmailCodeSent(false);
      toast.success('Email verificado!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Código incorreto');
    } finally { setVerifyingEmail(false); }
  };

  // ── Phone verification ──
  const handleSendPhoneCode = async () => {
    const phone = profile.phone;
    if (!phone || phone.length < 10) { toast.error('Digite um telefone válido'); return; }
    setSendingPhone(true);
    try {
      await axios.post(`${API_URL}/verify/phone/send`, { phone }, { withCredentials: true });
      setPhoneCodeSent(true);
      toast.success('SMS enviado!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar SMS');
    } finally { setSendingPhone(false); }
  };

  const handleVerifyPhone = async (code) => {
    setVerifyingPhone(true);
    try {
      await axios.post(`${API_URL}/verify/phone/check`, { phone: profile.phone, code }, { withCredentials: true });
      setPhoneVerified(true);
      setPhoneCodeSent(false);
      toast.success('Telefone verificado!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Código incorreto');
    } finally { setVerifyingPhone(false); }
  };

  // ── CEP Auto-fill ──
  const handleCepLookup = async (cep) => {
    const digits = cep.replace(/\D/g, '');
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await axios.get(`${API_URL}/cep/${digits}`);
      if (res.data.success) {
        const a = res.data.address;
        setProfile(p => ({
          ...p,
          address: {
            ...p.address,
            cep: a.cep || digits,
            street: a.street || p.address.street,
            neighborhood: a.neighborhood || p.address.neighborhood,
            city: a.city || p.address.city,
            state: a.state || p.address.state,
            complement: a.complement || p.address.complement,
          }
        }));
        toast.success('Endereço preenchido!');
      }
    } catch (e) {
      toast.error('CEP não encontrado');
    } finally { setCepLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/seller/profile`, profile, { withCredentials: true });
      if (res.data.success) { toast.success('Perfil salvo!'); navigate('/seller/dashboard'); }
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.join(', ') : d || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const handleSaveStep = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/seller/profile`, profile, { withCredentials: true });
      toast.success('Dados salvos!');
      if (step < STEPS.length - 1) setStep(s => s + 1);
      else { toast.success('Perfil completo!'); navigate('/seller/dashboard'); }
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.join(', ') : d || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50"><Navbar />
      <div className="flex items-center justify-center py-32"><Loader className="w-8 h-8 animate-spin text-blue-600" /></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="seller-complete-profile">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <button onClick={() => navigate('/seller/dashboard')} className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-2">
              <ArrowLeft className="w-4 h-4 mr-1" /> Voltar ao Dashboard
            </button>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="profile-title">Completar Cadastro</h1>
            <p className="text-sm text-gray-500 mt-1">Preencha seus dados para ativar todas as funcionalidades</p>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-8" data-testid="progress-bar">
          <div className="flex items-center justify-between mb-3">
            {STEPS.map((s, i) => (
              <button key={s.id} onClick={() => setStep(i)}
                className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                  i === step ? 'text-blue-600' : i < step ? 'text-emerald-600' : 'text-gray-400'
                }`} data-testid={`step-${s.id}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  i < step ? 'bg-emerald-100 text-emerald-600' : i === step ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'
                }`}>
                  {i < step ? <CheckCircle className="w-4 h-4" /> : i + 1}
                </div>
                <span className="hidden sm:inline">{s.title}</span>
              </button>
            ))}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div className="bg-blue-600 h-1.5 rounded-full transition-all duration-500" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 md:p-8">
          {/* ── Step 1: Dados Pessoais ── */}
          {step === 0 && (
            <div className="space-y-5" data-testid="step-personal-form">
              <div className="flex items-center gap-2 mb-4">
                <User className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Dados do Lojista</h2>
              </div>

              <div>
                <Label>Nome completo ou Razão Social *</Label>
                <Input value={profile.full_name} onChange={e => updateField('full_name', e.target.value)}
                  placeholder="Maria Silva ou Empresa LTDA" className="mt-1" data-testid="input-full-name" />
              </div>

              <div>
                <Label>Nome da Loja *</Label>
                <Input value={profile.store_name} onChange={e => updateField('store_name', e.target.value)}
                  placeholder="Minha Loja Incrível" className="mt-1" data-testid="input-store-name" />
              </div>

              <div>
                <Label>Tipo de Documento</Label>
                <div className="flex gap-3 mt-1">
                  {['cpf', 'cnpj'].map(t => (
                    <button key={t} type="button"
                      onClick={() => { updateField('document_type', t); updateField('document_number', ''); }}
                      className={`flex-1 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                        profile.document_type === t ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600'
                      }`} data-testid={`doc-type-${t}`}
                    >{t.toUpperCase()}</button>
                  ))}
                </div>
              </div>

              <div>
                <Label>{profile.document_type === 'cpf' ? 'CPF' : 'CNPJ'}</Label>
                <Input
                  value={profile.document_type === 'cpf' ? fmt.cpf(profile.document_number) : fmt.cnpj(profile.document_number)}
                  onChange={e => updateField('document_number', e.target.value.replace(/\D/g, ''))}
                  placeholder={profile.document_type === 'cpf' ? '000.000.000-00' : '00.000.000/0000-00'}
                  className="mt-1" data-testid="input-document" />
              </div>

              {/* Email Verification */}
              <div>
                <Label className="flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Email
                  {emailVerified && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Verificado</span>}
                </Label>
                <Input disabled value={userEmail} className="mt-1 bg-gray-50" />
                {!emailVerified && (
                  <div className="mt-2">
                    {!emailCodeSent ? (
                      <Button size="sm" variant="outline" onClick={handleSendEmailCode} disabled={sendingEmail} data-testid="send-email-code-btn">
                        {sendingEmail ? <Loader className="w-3 h-3 animate-spin mr-1" /> : <Mail className="w-3 h-3 mr-1" />}
                        Enviar código de verificação
                      </Button>
                    ) : (
                      <div>
                        <p className="text-xs text-blue-600 mb-1">Código enviado para {userEmail}</p>
                        <CodeInput onSubmit={handleVerifyEmail} loading={verifyingEmail} label="email" />
                        <button onClick={handleSendEmailCode} className="text-xs text-gray-400 hover:text-gray-600 mt-1">Reenviar código</button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Phone Verification */}
              <div>
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4" /> Telefone
                  {phoneVerified && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Verificado</span>}
                </Label>
                <Input value={fmt.phone(profile.phone)} onChange={e => updateField('phone', e.target.value.replace(/\D/g, ''))}
                  placeholder="(00) 00000-0000" className="mt-1" data-testid="input-phone" />
                {!phoneVerified && profile.phone.length >= 10 && (
                  <div className="mt-2">
                    {!phoneCodeSent ? (
                      <Button size="sm" variant="outline" onClick={handleSendPhoneCode} disabled={sendingPhone} data-testid="send-phone-code-btn">
                        {sendingPhone ? <Loader className="w-3 h-3 animate-spin mr-1" /> : <Phone className="w-3 h-3 mr-1" />}
                        Enviar SMS de verificação
                      </Button>
                    ) : (
                      <div>
                        <p className="text-xs text-blue-600 mb-1">SMS enviado para {fmt.phone(profile.phone)}</p>
                        <CodeInput onSubmit={handleVerifyPhone} loading={verifyingPhone} label="phone" />
                        <button onClick={handleSendPhoneCode} className="text-xs text-gray-400 hover:text-gray-600 mt-1">Reenviar SMS</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Step 2: Endereço ── */}
          {step === 1 && (
            <div className="space-y-5" data-testid="step-address-form">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Endereço</h2>
              </div>

              <div>
                <Label>CEP</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={fmt.cep(profile.address.cep || '')}
                    onChange={e => {
                      const val = e.target.value.replace(/\D/g, '');
                      updateAddress('cep', val);
                      if (val.length === 8) handleCepLookup(val);
                    }}
                    placeholder="00000-000" className="w-40" data-testid="input-cep" />
                  <Button size="sm" variant="outline" onClick={() => handleCepLookup(profile.address.cep)}
                    disabled={cepLoading || (profile.address.cep || '').replace(/\D/g, '').length !== 8}
                    data-testid="cep-search-btn">
                    {cepLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
                {cepLoading && <p className="text-xs text-blue-500 mt-1">Buscando endereço...</p>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2">
                  <Label>Rua</Label>
                  <Input value={profile.address.street || ''} onChange={e => updateAddress('street', e.target.value)} placeholder="Rua das Flores" className="mt-1" data-testid="input-street" />
                </div>
                <div>
                  <Label>Número</Label>
                  <Input value={profile.address.number || ''} onChange={e => updateAddress('number', e.target.value)} placeholder="100" className="mt-1" data-testid="input-number" />
                </div>
              </div>

              <div>
                <Label>Complemento</Label>
                <Input value={profile.address.complement || ''} onChange={e => updateAddress('complement', e.target.value)} placeholder="Apto, sala, bloco..." className="mt-1" data-testid="input-complement" />
              </div>

              <div>
                <Label>Bairro</Label>
                <Input value={profile.address.neighborhood || ''} onChange={e => updateAddress('neighborhood', e.target.value)} placeholder="Centro" className="mt-1" data-testid="input-neighborhood" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2">
                  <Label>Cidade</Label>
                  <Input value={profile.address.city || ''} onChange={e => updateAddress('city', e.target.value)} placeholder="São Paulo" className="mt-1" data-testid="input-city" />
                </div>
                <div>
                  <Label>Estado</Label>
                  <select value={profile.address.state || ''} onChange={e => updateAddress('state', e.target.value)}
                    className="mt-1 w-full h-10 rounded-md border border-gray-300 px-3 text-sm" data-testid="input-state">
                    <option value="">UF</option>
                    {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ── Step 3: Negócio ── */}
          {step === 2 && (
            <div className="space-y-5" data-testid="step-business-form">
              <div className="flex items-center gap-2 mb-4">
                <Briefcase className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Dados Comerciais</h2>
              </div>
              <div>
                <Label>Segmento da Loja *</Label>
                <select value={profile.business_segment} onChange={e => updateField('business_segment', e.target.value)}
                  className="mt-1 w-full h-10 rounded-md border border-gray-300 px-3 text-sm" data-testid="input-segment">
                  <option value="">Selecione o segmento</option>
                  {SEGMENTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label>Descrição do negócio (opcional)</Label>
                <textarea value={profile.business_description} onChange={e => updateField('business_description', e.target.value)}
                  placeholder="Descreva brevemente seu negócio..."
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="input-description" />
              </div>
            </div>
          )}

          {/* ── Step 4: Configurações ── */}
          {step === 3 && (
            <div className="space-y-5" data-testid="step-config-form">
              <div className="flex items-center gap-2 mb-4">
                <Settings className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Configurações Iniciais</h2>
              </div>
              <div>
                <Label>Nome da Vitrine *</Label>
                <Input value={profile.storefront_name} onChange={e => updateField('storefront_name', e.target.value)}
                  placeholder="Como sua loja aparecerá para os clientes" className="mt-1" data-testid="input-storefront-name" />
                <p className="text-xs text-gray-500 mt-1">Este nome aparecerá no topo da sua vitrine</p>
              </div>
              <div>
                <Label>Categoria Principal</Label>
                <select value={profile.main_category} onChange={e => updateField('main_category', e.target.value)}
                  className="mt-1 w-full h-10 rounded-md border border-gray-300 px-3 text-sm" data-testid="input-main-category">
                  <option value="">Selecione</option>
                  {SEGMENTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              {/* Resumo */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mt-6">
                <h3 className="text-sm font-semibold text-blue-800 mb-3">Resumo do cadastro</h3>
                <div className="grid grid-cols-2 gap-2 text-xs text-blue-700">
                  <div><span className="font-medium">Nome:</span> {profile.full_name || '—'}</div>
                  <div><span className="font-medium">Loja:</span> {profile.store_name || '—'}</div>
                  <div><span className="font-medium">Doc:</span> {profile.document_number ? fmt[profile.document_type](profile.document_number) : '—'}</div>
                  <div><span className="font-medium">Telefone:</span> {profile.phone ? fmt.phone(profile.phone) : '—'}</div>
                  <div><span className="font-medium">Cidade:</span> {profile.address.city || '—'}</div>
                  <div><span className="font-medium">Segmento:</span> {profile.business_segment || '—'}</div>
                  <div className="col-span-2 flex items-center gap-3 mt-1">
                    <span className={`flex items-center gap-1 ${emailVerified ? 'text-emerald-600' : 'text-amber-600'}`}>
                      <Mail className="w-3 h-3" /> Email {emailVerified ? 'verificado' : 'não verificado'}
                    </span>
                    <span className={`flex items-center gap-1 ${phoneVerified ? 'text-emerald-600' : 'text-amber-600'}`}>
                      <Phone className="w-3 h-3" /> Tel {phoneVerified ? 'verificado' : 'não verificado'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t">
            <Button variant="ghost" onClick={() => step > 0 ? setStep(s => s - 1) : navigate('/seller/dashboard')}
              className="text-gray-600" data-testid="prev-step-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> {step > 0 ? 'Anterior' : 'Voltar'}
            </Button>
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleSave} disabled={saving} data-testid="save-later-btn">
                <Save className="w-4 h-4 mr-1" /> Salvar
              </Button>
              <Button onClick={handleSaveStep} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="next-step-btn">
                {saving ? <Loader className="w-4 h-4 animate-spin mr-1" /> : null}
                {step < STEPS.length - 1 ? (<>Próximo <ChevronRight className="w-4 h-4 ml-1" /></>) : 'Finalizar Cadastro'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
